import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useBlocker } from "react-router-dom";
import {
  CalendarDays,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  ChevronLeft,
  User,
  Users,
  MapPin,
  MessageSquare,
} from "lucide-react";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useDirtyGuard } from "@/lib/dirty-guard";
import { useApp } from "@/contexts/AppContext";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase, clearSessionPersistenceFlags } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";
import { useLiveToday } from "@/features/calendar/hooks/useLiveToday";
import { useCalendarStore } from "@/store/useCalendarStore";
import { formatDate } from "@/lib/dateManager";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import { useIsFetching } from "@tanstack/react-query";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { useIntl } from "react-intl";
import type { MessageId } from "@/i18n/messages/en";
import {
  SidebarExtrasProvider,
  SidebarExtrasSlot,
} from "@/components/layout/SidebarExtras";

// ─────────────────────────────────────────────
// NAV CONFIG
// ─────────────────────────────────────────────

const navItems = [
  { labelId: "app.nav.calendar", icon: CalendarDays, path: "/dashboard" },
  { labelId: "app.nav.analytics", icon: BarChart3, path: "/analytics" },
  { labelId: "app.nav.circles", icon: Users, path: "/circles" },
  { labelId: "app.nav.settings", icon: Settings, path: "/settings" },
] satisfies Array<{
  labelId: MessageId;
  icon: typeof CalendarDays;
  path: string;
}>;

const pageTitleIds: Record<string, MessageId> = {
  "/dashboard": "app.nav.calendar",
  "/analytics": "app.nav.analytics",
  "/circles": "app.nav.circles",
  "/settings": "app.nav.settings",
  "/profile": "app.page.profile",
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function getInitials(first: string, last: string): string {
  if (!first && !last) return "";
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
}

// ─────────────────────────────────────────────
// USER DROPDOWN
// ─────────────────────────────────────────────

interface UserDropdownProps {
  profile: {
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl: string;
    country?: string;
    workLocation?: string;
  };
  initials: string;
  isSigningOut: boolean;
  onSignOut: () => void;
  onNavigate: (path: string) => void;
}

function UserDropdown({ profile, initials, isSigningOut, onSignOut, onNavigate }: UserDropdownProps) {
  const locationLabel = profile.workLocation || profile.country || null;
  const intl = useIntl();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-transform hover:scale-105 active:scale-95"
          aria-label={intl.formatMessage({ id: "app.userMenu.open" })}
        >
          <Avatar className="h-9 w-9 ring-2 ring-background shadow-sm cursor-pointer">
            <AvatarImage src={profile.avatarUrl} className="object-cover" />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="w-64 p-0 overflow-hidden">
        {/* User card header */}
        <div className="px-4 py-3.5 bg-muted/40 border-b border-border/60">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm flex-shrink-0">
              <AvatarImage src={profile.avatarUrl} className="object-cover" />
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight truncate">
                {profile.firstName} {profile.lastName}
              </p>
              {locationLabel && (
                <div className="flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <p className="text-xs text-muted-foreground truncate">{locationLabel}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-1.5">
          {/* Sprint 3 — Avatar menu is now strictly identity + account
              actions (Profile, Sign Out). Appearance/theme moved to
              /settings so display preferences live next to the rest of
              the "how the app works" toggles. */}
          <DropdownMenuItem onClick={() => onNavigate("/profile")} className="gap-2.5 rounded-lg cursor-pointer">
            <User className="h-4 w-4 text-muted-foreground" />
            <span>{intl.formatMessage({ id: "app.userMenu.profile" })}</span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="my-0" />

        <div className="p-1.5">
          <DropdownMenuItem
            onClick={onSignOut}
            disabled={isSigningOut}
            className="gap-2.5 rounded-lg cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <LogOut className="h-4 w-4" />
            <span>
              {intl.formatMessage({
                id: isSigningOut ? "app.userMenu.signingOut" : "app.userMenu.signOut",
              })}
            </span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─────────────────────────────────────────────
// HEADER RIGHT SIDE
// Order: NotificationBell | FeedbackButton | separator | [name] UserDropdown
// ─────────────────────────────────────────────

interface HeaderRightProps {
  profile: { firstName: string; lastName: string; email: string; avatarUrl: string; country?: string; workLocation?: string; };
  initials: string;
  isSigningOut: boolean;
  onSignOut: () => void;
  showName?: boolean;
  onFeedback: () => void;
  onNavigate: (path: string) => void;
}

function HeaderRight({ profile, initials, isSigningOut, onSignOut, showName, onFeedback, onNavigate }: HeaderRightProps) {
  const intl = useIntl();

  return (
    <div className="flex items-center gap-1">
      {/* NotificationBell queries an unbounded stream of notifications and
          performs optimistic updates — if the bell crashes (bad payload,
          realtime hiccup) we must NOT take the header down with it.
          The `fallback={() => null}` keeps the header layout untouched. */}
      <FeatureErrorBoundary
        feature="notification-bell"
        fallback={() => null}
      >
        <NotificationBell />
      </FeatureErrorBoundary>

      {/* Feedback button */}
      <button
        onClick={onFeedback}
        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors tap-highlight no-select"
        aria-label={intl.formatMessage({ id: "app.feedback.send" })}
        title={intl.formatMessage({ id: "app.feedback.title" })}
      >
        <MessageSquare className="h-4 w-4" />
      </button>

      {/* Separator */}
      <div className="w-px h-5 bg-border/60 mx-1 sm:mx-2" />

      {/* Name (desktop only) */}
      {showName && (
        <span className="text-sm text-muted-foreground no-select hidden sm:block mr-1">
          {profile.firstName} {profile.lastName}
        </span>
      )}

      <UserDropdown
        profile={profile}
        initials={initials}
        isSigningOut={isSigningOut}
        onSignOut={onSignOut}
        onNavigate={onNavigate}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN LAYOUT
// ─────────────────────────────────────────────

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { profile, setProfile, setAuthenticated, isAuthenticated, refreshUserData } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const intl = useIntl();

  const [collapsed, setCollapsed] = useState(false);
  const { viewMode, setViewMode } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const isFetching = useIsFetching();

  // On mobile, quarter grid is too dense — fall back to month unless goal is weekly.
  useEffect(() => {
    if (!isMobile) return;
    if (viewMode === "quarter") {
      setViewMode("month");
    }
  }, [isMobile, viewMode, setViewMode]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Timezone auto-sync ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detectedTz || detectedTz === profile.timezone) return;

    const sync = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { error } = await supabase
          .from("profiles")
          .update({ timezone: detectedTz })
          .eq("id", session.user.id);

        if (error) {
          return;
        }

        setProfile({ ...profile, timezone: detectedTz });
      } catch (_err) {
        // Non-critical background sync failure
      }
    };

    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile.timezone]);

  // ── Live-today auto-advance ────────────────────────────────────────────
  // A tab plausibly stays open across a whole workday (or longer), so the
  // calendar's navigation anchor (`currentDate`) can silently roll past a
  // week/month/quarter boundary while the user isn't looking. If the store
  // was still parked on what *used to be* "today" right before the
  // rollover, follow it forward so goal-period stats/confetti/nav don't
  // keep reflecting a stale period. If the user had deliberately navigated
  // to a different day, leave their position alone.
  const liveToday = useLiveToday();
  const prevLiveTodayRef = useRef(liveToday);

  useEffect(() => {
    const prevLiveToday = prevLiveTodayRef.current;
    if (liveToday !== prevLiveToday) {
      const storeCurrentDateStr = formatDate(useCalendarStore.getState().currentDate);
      if (storeCurrentDateStr === prevLiveToday) {
        useCalendarStore.getState().goToday();
      }
    }
    prevLiveTodayRef.current = liveToday;
  }, [liveToday]);

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut();
      // Clear "Keep me signed in" bookkeeping so the next login starts
      // from a clean slate (no stale ephemeral intent carried over).
      clearSessionPersistenceFlags();
      setAuthenticated(false);
      navigate("/");
    } catch (_error) {
      // Sign out failure handled by silent redirection
    } finally {
      setIsSigningOut(false);
    }
  };

  const { isSettingsDirty, setSettingsDirty } = useDirtyGuard();
  const [isSaving, setIsSaving] = useState(false);

  // 1. NATIVE BROWSER PROTECTION (Reload/Close Tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSettingsDirty) {
        e.preventDefault();
        e.returnValue = ""; // Standard way to show confirm dialog
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSettingsDirty]);

  // 2. ROUTER TRANSITION BLOCKER (Internal Navigation)
  // useBlocker will intercept any navigation attempt while isSettingsDirty is true.
  const blocker = useBlocker(({ nextLocation }) => 
    isSettingsDirty && nextLocation.pathname !== location.pathname
  );

  const handleNavigateAttempt = (path: string) => {
    navigate(path);
  };

  const confirmDiscard = () => {
    setSettingsDirty(false);
    // Re-fetch everything from Supabase to revert context state
    refreshUserData();
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  // Holds the `AbortController` for any in-flight "save from dialog" round
  // trip. If the user spams Save, each call cancels the previous listeners
  // cleanly (audit W5). On unmount we abort too, so we never leak
  // `settings-saved` / `settings-save-error` listeners on `window`.
  const pendingSaveAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      pendingSaveAbortRef.current?.abort();
      pendingSaveAbortRef.current = null;
    };
  }, []);

  const confirmSave = async () => {
    // Cancel any listeners left from an earlier, still-pending save request.
    pendingSaveAbortRef.current?.abort();

    const controller = new AbortController();
    pendingSaveAbortRef.current = controller;

    setIsSaving(true);
    window.dispatchEvent(new CustomEvent("save-settings-request"));

    const teardown = () => {
      controller.abort();
      if (pendingSaveAbortRef.current === controller) {
        pendingSaveAbortRef.current = null;
      }
    };

    const handleSuccess = () => {
      teardown();
      setSettingsDirty(false);
      setIsSaving(false);
      if (blocker.state === "blocked") {
        blocker.proceed();
      }
    };

    const handleError = () => {
      teardown();
      setIsSaving(false);
    };

    // `{ signal }` removes the listener automatically when the controller
    // aborts — from teardown, from unmount, or from a later confirmSave().
    window.addEventListener("settings-saved", handleSuccess, { signal: controller.signal });
    window.addEventListener("settings-save-error", handleError, { signal: controller.signal });
  };

  const confirmCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };
  
  const titleId = pageTitleIds[location.pathname];
  const title = titleId ? intl.formatMessage({ id: titleId }) : "Office Rhythm";
  const initials = getInitials(profile.firstName, profile.lastName);

  const headerRightProps: Omit<HeaderRightProps, "showName"> = {
    profile, initials, isSigningOut,
    onSignOut: handleSignOut,
    onFeedback: () => setFeedbackOpen(true),
    onNavigate: handleNavigateAttempt,
  };

  // Sidebar width is locked across all routes for layout consistency.
  // w-72 (288px) comfortably fits the widest portal'd content (the
  // calendar's mini-calendar grid needs ~248px). Pages without extras
  // simply leave the bottom of the sidebar empty, which is preferable
  // to the main content jumping horizontally between routes.

  // ─────────────────────────────────────────────
  // MOBILE
  // ─────────────────────────────────────────────
  if (isMobile) {
    return (
      <SidebarExtrasProvider>
      <div className="min-h-screen flex flex-col">
        <OfflineBanner />
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground top-0 left-0">
          Skip to main content
        </a>
        <header
          className={cn(
            "sticky top-0 z-50 relative flex flex-col transition-colors border-b border-border/50",
            scrolled ? "glass" : "bg-background"
          )}
        >
          <div className="px-4 h-14 flex items-center justify-between">
            <button
              type="button"
              onClick={() => handleNavigateAttempt("/dashboard")}
              className="flex flex-1 items-center cursor-pointer text-left text-inherit hover:text-foreground/80 transition-colors group border-0 bg-transparent p-0"
              aria-label="Go to calendar"
            >
            <div className="relative w-6 h-6 mr-2 flex-shrink-0">
              <img 
                src="/app-icon.png" 
                alt="Office Rhythm" 
                className="absolute inset-0 w-full h-full object-contain dark:opacity-0 transition-opacity duration-300" 
              />
              <img 
                src="/app-icon-dark.png" 
                alt="Office Rhythm" 
                className="absolute inset-0 w-full h-full object-contain opacity-0 dark:opacity-100 transition-opacity duration-300" 
              />
            </div>
            <h1 className="text-base font-semibold no-select tracking-tight">
              {title}
            </h1>
            </button>
            <div className="flex flex-1 justify-end">
              <HeaderRight {...headerRightProps} onNavigate={handleNavigateAttempt} />
            </div>
          </div>
          
          
          
          {/* Global Fetching Indicator - Stealth Mode (Header Bottom) */}
          {isFetching > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] pointer-events-none overflow-hidden bg-transparent">
              <div className="h-full w-full bg-primary/40 animate-pulse" />
            </div>
          )}
        </header>

        <main id="main-content" className="flex-1 flex flex-col min-h-0 pb-20 animate-fade-in">
          <GlobalErrorBoundary>{children}</GlobalErrorBoundary>
        </main>

        <nav className="fixed bottom-0 inset-x-0 z-50 glass safe-bottom border-t border-border/50">
          <div className="flex items-center justify-around h-14">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              const label = intl.formatMessage({ id: item.labelId });
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigateAttempt(item.path)}
                  className={cn(
                    "flex flex-col items-center gap-0.5 px-3 py-1 no-select tap-highlight transition-colors",
                    active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Feedback dialog — mounted at layout level so it's always accessible */}
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        
        {/* Dirty State Guard Modal — Portal ensures it covers all layers */}
        {blocker.state === "blocked" && (
          <UnsavedChangesModal
            isOpen={true}
            onCancel={confirmCancel}
            onDiscard={confirmDiscard}
            onSave={confirmSave}
            isSaving={isSaving}
          />
        )}


      </div>
      </SidebarExtrasProvider>
    );
  }

  // ─────────────────────────────────────────────
  // DESKTOP
  // ─────────────────────────────────────────────
  return (
    <SidebarExtrasProvider>
    {/* ── Root viewport lock ──────────────────────────────────────────
        `h-screen overflow-hidden` is the critical change: it pins the
        root to exactly 100 vh and clips any child that tries to exceed
        it. That's what gives every descendant a deterministic height
        budget — without it, a tall child (like the Week view's 1440 px
        24-hour grid) grows the root past the viewport and the whole
        page body-scrolls, hiding the sidebar + header. With the root
        clipped, `flex-1 min-h-0` descendants carve up the fixed height
        and any internal scrolling happens in their own `overflow-y`
        containers (the Week view's hours grid being the obvious one).
        ────────────────────────────────────────────────────────────── */}
    <div className="h-screen flex flex-col overflow-hidden">
      <OfflineBanner />
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground top-0 left-0">
        Skip to main content
      </a>

      <aside
        className={cn(
          "sticky top-0 h-screen flex flex-col flex-shrink-0 border-r border-border bg-sidebar transition-all duration-300 z-40",
          collapsed ? "w-16" : "w-72"
        )}
      >
        <div className="h-14 flex items-center px-4 border-b border-sidebar-border flex-shrink-0">
          {!collapsed && (
            <button
              type="button"
              onClick={() => handleNavigateAttempt("/dashboard")}
              className="flex items-center cursor-pointer text-left text-inherit group border-0 bg-transparent p-0 px-0.5"
              aria-label="Go to calendar"
            >
            <div className="relative w-6 h-6 mr-2 flex-shrink-0">
              <img 
                src="/app-icon.png" 
                alt="Office Rhythm" 
                className="absolute inset-0 w-full h-full object-contain dark:opacity-0 transition-opacity duration-300" 
              />
              <img 
                src="/app-icon-dark.png" 
                alt="Office Rhythm" 
                className="absolute inset-0 w-full h-full object-contain opacity-0 dark:opacity-100 transition-opacity duration-300" 
              />
            </div>
              <h2 className="font-bold text-base text-sidebar-foreground no-select tracking-tight truncate">
                Office Rhythm
              </h2>
            </button>
            )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "p-1.5 rounded-lg hover:bg-sidebar-accent text-sidebar-foreground transition-colors no-select tap-highlight flex-shrink-0",
              collapsed ? "mx-auto" : "ml-auto"
            )}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Scrollable middle region: nav items + optional page-contributed
            extras (e.g. the calendar's Designation legend + mini-calendar).
            Kept as a single scroll container so long content (like the
            mini-calendar on short viewports) remains reachable. */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          <nav className="py-4 px-2.5 space-y-1">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              const label = intl.formatMessage({ id: item.labelId });
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigateAttempt(item.path)}
                  title={collapsed ? label : undefined}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all no-select tap-highlight",
                    active
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-sidebar-foreground hover:bg-sidebar-accent"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{label}</span>}
                </button>
              );
            })}
          </nav>
          {!collapsed && (
            <SidebarExtrasSlot className="px-[var(--space-page-x)] pb-6 flex flex-col gap-7" />
          )}
        </div>

        {/* Sidebar bottom: Feedback button (expanded) or avatar (collapsed) */}
        <div className="p-2.5 border-t border-sidebar-border">
          {collapsed ? (
            <div className="flex justify-center">
              <Avatar className="h-8 w-8 ring-1 ring-border">
                <AvatarImage src={profile.avatarUrl} className="object-cover" />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </div>
          ) : (
            <button
              onClick={() => setFeedbackOpen(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent transition-all no-select tap-highlight"
            >
              <MessageSquare className="h-4 w-4 flex-shrink-0" />
              <span>{intl.formatMessage({ id: "app.feedback.title" })}</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main column — min-h-0 is critical here: without it, a flex
          child (e.g. a Week-view time grid with 1440 px of intrinsic
          content) can push the outer min-h-screen container past the
          viewport and cause the whole page to body-scroll. With
          min-h-0 the chain's `flex-1 min-h-0` descendants are free to
          shrink below their content size and handle overflow locally
          via their own scroll containers. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header
          // `flex-none` is deliberate — in a flex-col parent `flex-1`
          // would try to GROW the header vertically (a subtle bug that
          // only worked before because max-h-14 clamped the grow). With
          // `flex-none` + h-14 the row is rigidly sized and the
          // `flex-1` is reserved for <main> below, which is the only
          // child that should claim the remaining vertical space.
          className={cn(
            "sticky top-0 z-30 relative h-14 min-h-[3.5rem] max-h-14 flex flex-none items-center px-6 transition-colors border-b border-border/30",
            scrolled ? "glass" : "bg-background"
          )}
        >
          <div className="flex flex-1 items-center gap-4">
            <div className="flex items-center gap-3">
              <h1 className="text-base font-semibold no-select tracking-tight min-w-[max-content]">{title}</h1>
            </div>
          </div>
          
          {/* Header Center Panel (Absolute Center) */}
          {(location.pathname === "/dashboard" || location.pathname === "/analytics") && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-auto">
              <div className="flex bg-secondary/80 rounded-xl p-0.5">
                {(["week", "month", "quarter"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors no-select tap-highlight",
                      viewMode === mode
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-1 justify-end">
            <HeaderRight {...headerRightProps} showName />
          </div>
          
          {/* Global Fetching Indicator - Stealth Mode (Header Bottom) */}
          {isFetching > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] pointer-events-none overflow-hidden bg-transparent">
              <div className="h-full w-full bg-primary/40 animate-pulse" />
            </div>
          )}
        </header>

        {/* ── <main> is the native scroll surface ───────────────────
            Hybrid scrolling strategy:
              • The ROOT is `h-screen overflow-hidden` (see above) so
                the sidebar + header stay rigidly fixed at the viewport
                edges — no whole-page body scroll, ever.
              • <main> gets `flex-1 min-h-0 overflow-y-auto` so any
                ROUTE (Settings, Circles, Profiles, Analytics, …) can
                render its natural content height and scroll *inside*
                main when it overflows. To the user this reads as a
                normal page scroll under fixed chrome.
              • Pages that need a locked internal layout (the Calendar
                in particular) wrap themselves in `w-full h-full flex
                flex-col overflow-hidden`, which fills main's visible
                area and takes over scroll management locally — so the
                Week view's hours grid is the only thing that moves
                while on /dashboard.
            `flex flex-col` stays on main so child pages that opt into
            the flex chain (via flex-1 / min-h-0) continue to work —
            it's only `overflow-y-auto` that was missing before.
            ────────────────────────────────────────────────────── */}
        <main
          id="main-content"
          className="flex-1 flex flex-col min-h-0 w-full overflow-y-auto animate-fade-in bg-background/50"
        >
          <GlobalErrorBoundary>{children}</GlobalErrorBoundary>
        </main>
      </div>
    </div>

      {/* Global Modals & Dialogs */}
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      
      {/* Dirty State Guard Modal — Portal ensures it covers all layers */}
      {blocker.state === "blocked" && (
        <UnsavedChangesModal
          isOpen={true}
          onCancel={confirmCancel}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          isSaving={isSaving}
        />
      )}
    </div>
    </SidebarExtrasProvider>
  );
}
